import { Case } from '../../../api/case';
import * as api from '../../../api';
import './CaseStatus.css';
import { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../App';
import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import DueDate from '../../../components/due-date/DueDate';
import Input from '../../../components/input/Input';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../../../utils/date-utils';

interface Props {
  _case: Case;
  updateCase: (updates: Partial<Case>) => void;
}

const CaseStatus: React.FC<Props> = ({ _case, updateCase }) => {
  const ctx = useContext(AppContext);
  const canEditCaseDueDate =
    _case.process.allowCaseDueDateUpdate && _case.status !== 'COMPLETED' && _case.status !== 'CANCELLED';
  const canEditStartDate = ctx.user.roles.includes('admin');
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingStartDate, setEditingStartDate] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setEditingStartDate(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleSetDueDate = async (newDueDate: Date) => {
    const res = await api.cases.updateCaseDueDate(_case.id, newDueDate);
    if (res.data) {
      updateCase({ dueDate: res.data.dueDate });
      ctx.setToast({ message: 'Case due date updated', type: 'success', clearAfterMs: 3000 });
    }
  };

  const handleSetStartDate = async (newStartDate: Date) => {
    const res = await api.cases.updateCaseCreatedAt(_case.id, newStartDate);
    if (res.data) {
      updateCase({ createdAt: res.data.createdAt });
      ctx.setToast({ message: 'Case start date updated', type: 'success', clearAfterMs: 3000 });
      setEditingStartDate(false);
      setMenuOpen(false);
    }
  };

  const completionPercentage = _case.status === 'COMPLETED' ? 1 : _case.completionPercentage;
  const startDateDisplay = formatDate(_case.createdAt, ctx.user.company.locale, ctx.user.company.timezone);

  return (
    <div className="case-status box">
      <div>
        <div className="case-status__completion-percentage">
          <CircularProgressbarWithChildren
            value={completionPercentage * 100}
            styles={{
              path: { stroke: 'var(--green)' },
              text: { fill: 'var(--text)', fontSize: '18px' },
            }}
          >
            <p className="case-status__completion-percentage-text">{Math.round(completionPercentage * 100)}%</p>
          </CircularProgressbarWithChildren>
        </div>
      </div>

      <div>
        {!!_case.dueDate && (
          <div>
            <p style={{ fontSize: '12px' }}>Due Date</p>
            <DueDate
              date={_case.dueDate}
              onSetDueDate={canEditCaseDueDate ? handleSetDueDate : undefined}
            />
          </div>
        )}
        {!_case.dueDate && (
          <div>
            <p style={{ fontSize: '12px' }}>Due Date</p>
            {!canEditCaseDueDate && <p style={{ color: 'var(--gray-400)' }}>Not set</p>}
            {canEditCaseDueDate && <DueDate onSetDueDate={handleSetDueDate} />}
          </div>
        )}
      </div>

      {canEditStartDate && (
        <div className="case-status__menu" ref={menuRef}>
          <button
            type="button"
            className="case-status__menu-trigger"
            aria-label="Case date options"
            onClick={() => {
              setMenuOpen((open) => !open);
              setEditingStartDate(false);
            }}
          >
            <EllipsisVerticalIcon />
          </button>
          {menuOpen && (
            <div className="case-status__menu-dropdown">
              {!editingStartDate && (
                <button
                  type="button"
                  className="case-status__menu-item"
                  onClick={() => setEditingStartDate(true)}
                >
                  Start date · {startDateDisplay}
                </button>
              )}
              {editingStartDate && (
                <div className="case-status__menu-edit">
                  <Input
                    value={_case.createdAt ? new Date(_case.createdAt).toISOString() : ''}
                    onChange={async (value) => {
                      await handleSetStartDate(new Date(value));
                    }}
                    labelText="Start date"
                    id="case-start-date"
                    type="date"
                    small={true}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CaseStatus;
